import style from './CurrencyPickerField.module.scss';

import { Button, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogActions,
  DialogContent,
  useDialogs,
} from '@work4all/components';
import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import {
  CurrencyPicker,
  ICurrencyPickerProps,
} from '@work4all/components/lib/components/entity-picker/CurrencyPicker';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { Currency } from '@work4all/models/lib/Classes/Currency.entity';

import { formatCurrency } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { PickerTargetButton } from '../../containers/mask-overlays/locked-inputs';

interface Props extends Omit<ICurrencyPickerProps<false>, 'multiple'> {
  error?: string;
  disabled?: boolean;
  course?: number;
  courseDate?: string;
  defaultCurrency?: Currency;
  onCourseChange?: (value: number) => void;
}

const format3Numbers = (value: number, locale: string) =>
  value.toLocaleString(locale, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

export const CurrencyPickerField = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { t, i18n } = useTranslation();
    const {
      error,
      clearable,
      course,
      courseDate,
      disabled,
      defaultCurrency,
      onCourseChange,
      ...pickerProps
    } = props;
    const { value, onChange } = pickerProps;
    const date = courseDate
      ? DateTime.fromISO(courseDate).toLocaleString(DateTimeCustom.DATE_SHORT)
      : '';

    const openCurrencyDialog = useCurrencyDialog({
      course,
      date,
      currentCurrency: pickerProps.value,
      defaultCurrency,
      onSubmit: onCourseChange,
    });
    const actions = useMemo(() => {
      if (course && defaultCurrency?.name !== pickerProps.value?.name) {
        return [
          {
            href: undefined,
            icon: (
              <Typography variant="body2" color="primary">
                {t('COMMON.COURSE_WITH_VALUE', {
                  value: format3Numbers(course, i18n.language),
                  date: date ? ` | ${date}` : '',
                })}
              </Typography>
            ),
            allowDisabled: true,
            className: style.action,
            onClick: openCurrencyDialog,
          },
        ];
      }
      return [];
    }, [
      i18n,
      course,
      date,
      openCurrencyDialog,
      pickerProps.value?.name,
      defaultCurrency?.name,
    ]);

    return (
      <EntityPickerPopover
        picker={<CurrencyPicker {...pickerProps} multiple={false} />}
      >
        <PickerTargetButton
          ref={ref}
          onClear={
            clearable
              ? () => {
                  onChange(null);
                }
              : undefined
          }
          label={t('INPUTS.CURRENCY')}
          error={error}
          value={(value && formatCurrency(value, t)) || ''}
          disabled={disabled}
          actions={actions}
          actionsWrapperClassName={style.actionsWrapperClassName}
        />
      </EntityPickerPopover>
    );
  }
);
interface UseCurrencyDialogProps {
  course: number;
  date: string;
  currentCurrency: Currency;
  defaultCurrency: Currency;
  onSubmit: (value: number) => void;
}

const useCurrencyDialog = (props: UseCurrencyDialogProps) => {
  const { course, date, currentCurrency, defaultCurrency, onSubmit } = props;
  const dialogs = useDialogs();

  return useCallback(() => {
    const { id } = dialogs.open(CurrencyDialog, {
      onClose: () => {
        dialogs.close(id);
      },
      course,
      date,
      currentCurrency,
      defaultCurrency,
      onSubmit,
    });
  }, [dialogs, course, date, currentCurrency, defaultCurrency, onSubmit]);
};

interface CurrencyDialogProps extends UseCurrencyDialogProps {
  onClose?: () => void;
  open: boolean;
}

const CurrencyDialog = (props: CurrencyDialogProps) => {
  const {
    onClose,
    open,
    course,
    date,
    currentCurrency,
    defaultCurrency,
    onSubmit,
  } = props;
  const { t, i18n } = useTranslation();

  const [value, setValue] = useState(Number(course.toFixed(3)));
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>
        <div className={style.dialog}>
          <div className={style.dialogInputs}>
            <NumberInput
              value={value}
              onChange={setValue}
              label={t('COMMON.COURSE')}
              hideControls
              decimals={3}
              unit={formatCurrency(currentCurrency, t)}
            />
            <LabeledInput disabled label={t('COMMON.DATE')} value={date} />
          </div>
          {currentCurrency && defaultCurrency && (
            <div className={style.dialogInfo}>
              <Typography>{`1 ${formatCurrency(
                currentCurrency,
                t
              )} `}</Typography>
              <Typography color="secondary">
                {t('COMMON.CORRESPONDS')}
              </Typography>
              <Typography>{`${format3Numbers(
                value,
                i18n.language
              )} ${formatCurrency(defaultCurrency, t)}`}</Typography>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ALERTS.BTN_ABORT')}</Button>
        <Button
          disabled={value === course}
          onClick={() => {
            onSubmit(value);
            onClose();
          }}
        >
          {t('ALERTS.BTN_SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
